import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { createApi } from 'ReduxHelpers';
import { type AnyObject } from '@Types';
import pick from 'Utils/pick';
import { lessonLaunchPath, streamDashboardPath } from './contentPaths';
import {
    type StreamIndexResponse,
    type StreamShowResponse,
    type LinkToLessonInStream,
    type StreamCompoundItemVersionKey,
    type StreamForStreamDashboardShowResponse,
    type StreamCardIndexResponse,
    type ExternalLinkMetadataIndexResponse,
} from './Lessons.types';

enum TagType {
    publishedStream = 'publishedStream',
}

export const getLessonLinksStreamFields = ['id', 'title', 'chapters', 'lessons', 'image', 'localePack'] as const;
export const getLessonLinksLessonFields = ['id', 'title'] as const;

/*
    Enough to render a recommended or related course card and to evaluate whether it can be launched.

    NOTE the casing difference between these two. snakeCaseQueryBody converts every body key to snake
    case, and additionally converts the *values* of `fields` from camelCase to snake case. It does
    not do that for the values of `lessonFields`, so those have to be written snake-cased already or
    the server drops them silently in `@selects.slice!(*fields)`.
*/
const streamCardFields = ['id', 'title', 'image', 'comingSoon', 'localePack', 'exam', 'chapters', 'lessons'] as const;
const streamCardLessonFields = ['id', 'title', 'locale_pack', 'assessment', 'test'] as const;

/*
    The server sends locale_pack but not locale_pack_id. Derive localePackId here: ContentAccess and the
    progress lookups key on it and fail quietly without it.
*/
function deriveLocalePackIds<T extends { localePack?: { id: string }; lessons?: { localePack?: { id: string } }[] }>(
    stream: T,
): T {
    return {
        ...stream,
        localePackId: stream.localePack?.id,
        lessons: stream.lessons?.map(lesson => ({ ...lesson, localePackId: lesson.localePack?.id })),
    };
}

// This API is intended to be used in user-facing code. In order to prevent us from importing editor-only code
// into the student bundle, there is a separate LessonsEditorApi
/*
    Mirrors Lesson.Stream#_optionsForGetCalls, which every dashboard read went through.

    Three details in there are easy to get wrong:

    1. Signed out, it FORCES includeProgress to false, overriding whatever the caller asked for. The
       server raises "user must be provided in order to include progress" otherwise.
    2. cohortIds is only sent when the user has at least one relevant cohort. Sending an empty array
       filters everything out rather than filtering nothing.
    3. Signed in, cohortIds is the ONLY filter it adds. It does not add inLocaleOrEn, userCanSee or
       published, so neither do we. Adding userCanSee or published would quietly narrow what a
       signed-in user may load, and published would hide a working version from an admin.

    Pass userCohortIds as null for a signed-out request, and as an array (possibly empty) otherwise.
*/
function optionsForGetCalls({
    userCohortIds,
    prefLocale,
    filters = {},
}: {
    userCohortIds: string[] | null;
    prefLocale: string;
    filters?: AnyObject;
}) {
    if (userCohortIds === null) {
        return {
            includeProgress: false,
            filters: { ...filters, inLocaleOrEn: prefLocale, userCanSee: null, inUsersLocaleOrEn: null },
        };
    }

    return {
        includeProgress: true,
        filters: userCohortIds.length > 0 ? { ...filters, cohortIds: userCohortIds } : filters,
    };
}

export const lessonsApi = createApi('LessonsApi', {
    ...getBackRoyalApiBaseConfig('LessonsApi'),
    tagTypes: Object.values(TagType),

    /*
            Flatten out all of the lessons in the stream index call into an object
            that has enough information to build a link to the lesson. This assumes that
            each lesson only appears in a single stream for a given user, since the
            response is keyed off of lessonId
        */
    // oxlint-disable-next-line max-lines-per-function
    endpoints: builder => ({
        getLinksToLessonsInStreams: builder.query<Record<string, LinkToLessonInStream>, void>({
            query: () => ({
                url: 'lesson_streams/index.json',
                method: 'POST',
                body: {
                    filters: { user_can_see: true, locale: 'en' },
                    fields: getLessonLinksStreamFields,
                    lesson_fields: getLessonLinksLessonFields,
                },
            }),
            transformResponse: (
                response: StreamIndexResponse<
                    (typeof getLessonLinksStreamFields)[number],
                    (typeof getLessonLinksLessonFields)[number]
                >,
            ) => {
                const lessonLinks: Record<string, LinkToLessonInStream> = {};
                response.contents.lessonStreams.forEach(stream => {
                    let lessonIndex = 0;
                    stream.chapters.forEach((chapter, chapterIndex) => {
                        chapter.lessonIds.forEach(lessonId => {
                            const lesson = stream.lessons.find(l => l.id === lessonId);
                            if (!lesson) {
                                // FIXME: log error
                                return;
                            }
                            lessonLinks[lessonId] = {
                                streamAttributes: pick(stream, ['id', 'title', 'localePack']),
                                streamDashboardPath: streamDashboardPath({
                                    streamTitle: stream.title,
                                    streamId: stream.id,
                                }),
                                lessonLaunchPath: lessonLaunchPath({ lessonId, streamId: stream.id, chapterIndex }),
                                lessonIndexInStream: lessonIndex,
                                lessonTitle: lesson.title,
                                streamTitle: stream.title,
                                streamImage: stream.image,
                            };
                            lessonIndex += 1;
                        });
                    });
                });
                return lessonLinks;
            },
        }),

        getPublishedStream: builder.query<
            StreamShowResponse,
            {
                id: string;
                loadFullContentForLessonId?: string | null;
                loadFullContentForAllLessons?: boolean;
                userCohortIds: string[] | null;
                prefLocale: string;
            }
        >({
            query: ({ id, loadFullContentForLessonId, loadFullContentForAllLessons, userCohortIds, prefLocale }) => {
                let filters: AnyObject = { inLocaleOrEn: prefLocale };
                if (userCohortIds) {
                    filters = {
                        ...filters,
                        userCanSee: true,
                        published: true,
                        cohortIds: userCohortIds,
                    };
                } else {
                    filters = {
                        ...filters,
                        userCanSee: null,
                    };
                }
                return {
                    url: `lesson_streams/${id}.json`,
                    method: 'GET',
                    params: {
                        filters,
                        loadFullContentForLessonId,
                        loadFullContentForAllLessons,
                    },
                };
            },
            providesTags: (_result, _error, { id }) => [{ type: TagType.publishedStream, id }],
        }),

        /*
            The payload behind the stream dashboard.

            Pass userCohortIds as null for a signed-out request. optionsForGetCalls decides both the
            filters and includeProgress from it, so neither is a parameter here.
        */
        getStreamForDashboard: builder.query<
            StreamForStreamDashboardShowResponse,
            {
                id: string;
                userCohortIds: string[] | null;
                prefLocale: string;
            }
        >({
            query: ({ id, userCohortIds, prefLocale }) => ({
                url: `lesson_streams/${id}.json`,
                method: 'GET',
                params: optionsForGetCalls({ userCohortIds, prefLocale }),
            }),
            transformResponse: (response: StreamForStreamDashboardShowResponse) => ({
                contents: { lessonStreams: response.contents.lessonStreams.map(deriveLocalePackIds) },
            }),
            providesTags: (_result, _error, { id }) => [{ type: TagType.publishedStream, id }],
        }),

        /*
            The recommended and related course cards. Mirrors Stream.indexForCurrentUser with
            filters {published: true, id: [...]}.

            Progress rides along for a signed-in student, so that following one of these links lands on
            a page that already knows where they are. A visitor gets no progress: asking for it makes
            the server raise.
        */
        getStreamsByIds: builder.query<
            StreamCardIndexResponse,
            { ids: string[]; userCohortIds: string[] | null; prefLocale: string }
        >({
            query: ({ ids, userCohortIds, prefLocale }) => ({
                url: 'lesson_streams/index.json',
                method: 'POST',
                body: {
                    ...optionsForGetCalls({ userCohortIds, prefLocale, filters: { published: true, id: ids } }),
                    fields: streamCardFields,
                    lessonFields: streamCardLessonFields,
                },
            }),
            transformResponse: (response: StreamCardIndexResponse) => ({
                contents: { lessonStreams: response.contents.lessonStreams.map(deriveLocalePackIds) },
            }),
        }),

        /*
            Build the query string by hand. serializeParams emits cohort_ids[0]=a, which Rails parses as a
            hash, and permit(cohort_ids: []) drops it silently.
            Send every relevant cohort: a student can open a stream from any of their cohorts.
        */
        getExternalLinksForStream: builder.query<
            ExternalLinkMetadataIndexResponse,
            { streamId: string; userCohortIds: string[] | null }
        >({
            query: ({ streamId, userCohortIds }) => {
                const search = new URLSearchParams({ stream_id: streamId });
                (userCohortIds ?? []).forEach(cohortId => search.append('cohort_ids[]', cohortId));

                return { url: `external_link_metadata.json?${search.toString()}`, method: 'GET' };
            },
        }),

        // It's maybe a little odd that this is a mutation, but being a mutation allows it
        // to invalidate the tags for the streams that the server tells us are out of date.
        invalidateOutdatedStreams: builder.mutation<
            StreamIndexResponse<'id', never>,
            { id: string; compoundItemVersionKey: StreamCompoundItemVersionKey }[]
        >({
            query: streamParams => ({
                url: 'lesson_streams/get_outdated.json',
                method: 'POST',
                body: {
                    streams: streamParams,
                    fields: ['id'],
                    limit: 0, // No need to limit since we're just loading ids
                },
            }),

            invalidatesTags: result =>
                result?.contents?.lessonStreams?.map(({ id }) => ({ type: TagType.publishedStream, id })) || [],
        }),
    }),
});
